import React, { Component } from 'react';
import L, { LatLngExpression } from 'leaflet';
import { Map as LMap, TileLayer } from 'react-leaflet';
import styled from 'styled-components';
import { observer } from 'mobx-react';

import KarteStore from '../../store';
import { MarkerIconStyles } from '../../style';
import { Category, PopulationDataType, ValueType, Format } from '../../types';

import POI from './POI';
import Polygons from './Polygons';
import RankingLegend from './RankingLegend';
import PopulationLegend from './PopulationLegend';
import SimilarityDescription from './SimilarityDescription';

import iconRetinaUrl from 'leaflet/dist/images/marker-icon-2x.png';
import iconUrl from 'leaflet/dist/images/marker-icon.png';
import shadowUrl from 'leaflet/dist/images/marker-shadow.png';

delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({ iconRetinaUrl, iconUrl, shadowUrl });

type Props = {
  karteIndex: number;
  karteStore: KarteStore;
};

type State = {
  selectedAreaName?: string;
};

const mapTileUrl = 'https://cyberjapandata.gsi.go.jp/xyz/pale/{z}/{x}/{y}.png';
const defaultPosition: LatLngExpression = {
  lat: 35.583127894215558,
  lng: 139.649565555182846
};

@observer
class Map extends Component<Props, State> {
  state: State = {};

  render() {
    const { karteStore, karteIndex } = this.props;
    const { selectedAreaName } = this.state;
    const {
      karte,
      position,
      geoData,
      values,
      poiData,
      legends
    } = karteStore.getKarteMapState(karteIndex);
    const ranking = karteStore.getRanking(karteIndex)?.(selectedAreaName);
    const {
      similarity,
      top3AreaName,
      similarityAreas
    } = karteStore.getSimilarityCluster(karteIndex, selectedAreaName);

    return (
      <Wrapper>
        <MarkerIconStyles />
        <LMap
          style={{ height: '100%' }}
          center={position ?? defaultPosition}
          zoom={getZoom(karte.category)}
        >
          <TileLayer url={mapTileUrl} />
          <POI
            data={poiData}
            legendHidden={karte.format === Format.Similarity}
          />
          <Polygons
            karte={karte}
            data={geoData}
            values={values}
            similarityAreaNameList={similarityAreas}
            onAreaSelect={area => this.setState({ selectedAreaName: area })}
          />
          <RankingLegend
            year={karte.year}
            dataType={karte.populationDataType}
            targetAreaName={selectedAreaName ?? karte.area}
            targetAreaValue={
              values?.[this.state.selectedAreaName ?? karte.area]
            }
            ranking={ranking}
            isProportionValue={karte.valueType === ValueType.Proportion}
            isKu={karte.category === Category.Ku}
            isTotalDataType={
              karte.populationDataType === PopulationDataType.Total
            }
            isSimilarity={karte.format === Format.Similarity}
            supportType={karte.support}
          />
          <PopulationLegend
            legends={legends}
            isSimilarity={karte.format === Format.Similarity}
          />
          {karte.format === Format.Similarity && (
            <SimilarityDescription
              similarity={similarity}
              top3AreaName={top3AreaName}
            />
          )}
        </LMap>
      </Wrapper>
    );
  }
}

const Wrapper = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
  text-align: center;
`;

const getZoom = (category: Category) => {
  switch (category) {
    case Category.Chome:
      return 15;
    case Category.Chikuminjikyo:
      return 14;
    case Category.Hokatsushien:
    case Category.ElementarySchool:
      return 15;
    case Category.JuniorHighSchool:
      return 14;
    case Category.Mimamori:
      return 14;
    case Category.Ku:
      return 12;
    default:
      return 14;
  }
};

export default Map;
